// Third-party imports
import {
  TextInput,
  SelectInput,
  AutocompleteArrayInput,
  NumberInput,
  useEditContext,
  required,
  TabbedForm,
  FormTab,
} from 'react-admin';

// Local imports - types
import type { ValidCodeNameType } from '@src/types';

// Local imports - components
import CatalogEditToolbar from './CatalogEditToolbar';
import VariantsSection from './VariantsSection';
import AvailabilityInput from './AvailabilityInput';

// Form component that can access the record through Edit context
const CatalogForm = () => {
  const { record } = useEditContext();
  const attributes = record?.attributeData;

  // Create choices for select inputs
  const departmentChoices = attributes?.validDepartments?.map((dept: string) => ({
    id: dept,
    name: dept,
  })) || [];

  const categoryChoices = attributes?.validCategories?.map((cat: string) => ({
    id: cat,
    name: cat,
  })) || [];

  const genderChoices = attributes?.validGenders?.map((gender: ValidCodeNameType) => ({
    id: gender.code,
    name: gender.name,
  })) || [];

  const rubricCodeChoices = attributes?.validRubricCodes?.map((code: string) => ({
    id: code,
    name: code,
  })) || [];

  return (
    <TabbedForm toolbar={<CatalogEditToolbar />}>
      <FormTab label="Basic Information">
        <TextInput
          source="productName"
          label="Product Name"
          fullWidth
          validate={required()}
        />
        <TextInput
          source="productDescription"
          label="Product Description"
          fullWidth
          multiline
          rows={4}
        />
        <TextInput
          source="brandDisplay"
          label="Brand Display"
          fullWidth
        />
      </FormTab>

      <FormTab label="Categorization">
        <AutocompleteArrayInput
          source="departments"
          label="Departments"
          choices={departmentChoices}
          fullWidth
        />
        <AutocompleteArrayInput
          source="categories"
          label="Categories"
          choices={categoryChoices}
          fullWidth
        />
        <TextInput
          source="partnerCategory"
          label="Partner Category"
          fullWidth
        />
        <SelectInput
          source="gender.code"
          label="Gender"
          choices={genderChoices}
          optionText="name"
          optionValue="id"
          fullWidth
        />
      </FormTab>

      <FormTab label="Pricing & Details">
        <NumberInput
          source="msrpPrice"
          label="MSRP Price ($)"
          step={0.01}
          min={0}
          fullWidth
        />
        <TextInput
          source="taxCode"
          label="Tax Code"
          fullWidth
        />
        <AvailabilityInput
          source="availability"
          label="Availability"
        />
        <SelectInput
          source="rubricCode"
          label="Rubric Code"
          choices={rubricCodeChoices}
          optionText="name"
          optionValue="id"
          fullWidth
        />
      </FormTab>

      <FormTab label="Variants">
        <VariantsSection />
      </FormTab>
    </TabbedForm>
  );
};

export default CatalogForm;
